import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  DEPARTAMENTE,
  ETICHETE_TIP,
  departamentulRolului,
  numeDepartament,
  type TipCampanie,
} from '@b4l/shared'
import { api } from '../../../lib/api'
import { TON } from '../../ui/TonuriB4L'
import { CULOARE_TIP } from './activitate'
import { ziScurt } from './EchipaEveniment'

/**
 * Calendarul echipei (24.10.2026) — pentru Victor: cine e unde, pe zile. Un rand pe om, o coloana
 * pe zi, doua saptamani odata.
 *
 * Dupa research (Float, monday.com, Microsoft Shifts — vezi planul): hasura galbena = absent,
 * rosu doar pentru un om pus in doua locuri in aceeasi zi; iconita si text, nu doar culoare;
 * alocarea provizorie (activitate inca propunere) doar cu contur. Pe telefon, lista pe zile.
 */

interface Alocare {
  zi: string
  campanieId: number
  titlu: string
  tip: TipCampanie
  provizorie: boolean
  rol: string | null
}
interface Om {
  userId: number
  nume: string
  rolUser: string
  functie: string | null
  alocari: Alocare[]
  absente: string[]
}
interface Raspuns {
  absenta: 'concediu' | 'indisponibil'
  cuAbsente: boolean
  oameni: Om[]
}

const ZI = 86_400_000
const ZILE = 14
const iso = (t: number) => new Date(t).toISOString().slice(0, 10)

/** Lunea saptamanii, in UTC — zilele alocate sunt zile, fara ora. */
function luni(d: Date): number {
  const t = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
  return t - ((new Date(t).getUTCDay() + 6) % 7) * ZI
}

const weekend = (z: string) => [0, 6].includes(new Date(`${z}T00:00:00Z`).getUTCDay())

/** Zilele unui om: ce e in fiecare — alocarile, absenta. */
function peZile(o: Om) {
  const m = new Map<string, Alocare[]>()
  for (const a of o.alocari) m.set(a.zi, [...(m.get(a.zi) ?? []), a])
  return m
}

export function CalendarEchipa() {
  const azi = new Date()
  const [inceput, setInceput] = useState(() => luni(azi))
  const [dep, setDep] = useState<string>('toate')
  const [doarAlocati, setDoarAlocati] = useState(true)
  const [doarConflicte, setDoarConflicte] = useState(false)
  const zile = useMemo(
    () => Array.from({ length: ZILE }, (_, i) => iso(inceput + i * ZI)),
    [inceput],
  )
  const q = useQuery<Raspuns>({
    queryKey: ['echipa-calendar', zile[0], zile[ZILE - 1], 'toti'],
    queryFn: async () =>
      (
        await api.get<Raspuns>('/campanii/echipa/calendar', {
          params: { deLa: zile[0], panaLa: zile[ZILE - 1] },
        })
      ).data,
  })

  const oameni = useMemo(() => {
    const toti = q.data?.oameni ?? []
    return toti
      .map((o) => {
        const m = peZile(o)
        const dubluri = [...m.values()].filter((l) => l.length > 1).length
        const peste = o.alocari.filter((a) => o.absente.includes(a.zi)).length
        return { o, m, conflicte: dubluri + peste }
      })
      .filter(({ o, conflicte }) => {
        if (dep !== 'toate' && departamentulRolului(o.rolUser) !== dep) return false
        if (doarConflicte) return conflicte > 0
        if (doarAlocati) return o.alocari.length > 0
        return true
      })
  }, [q.data, dep, doarAlocati, doarConflicte])
  const totalConflicte = useMemo(
    () =>
      (q.data?.oameni ?? []).filter((o) => {
        const m = peZile(o)
        return (
          [...m.values()].some((l) => l.length > 1) ||
          o.alocari.some((a) => o.absente.includes(a.zi))
        )
      }).length,
    [q.data],
  )
  const tipuri = useMemo(
    () => [...new Set((q.data?.oameni ?? []).flatMap((o) => o.alocari.map((a) => a.tip)))],
    [q.data],
  )
  const absenta = q.data?.absenta === 'concediu' ? 'Concediu' : 'Indisponibil'
  const muta = (s: number) => setInceput(inceput + s * 7 * ZI)
  const aziIso = iso(Date.UTC(azi.getFullYear(), azi.getMonth(), azi.getDate()))

  const filtru = (on: boolean) =>
    `rounded-md border px-2.5 py-1 text-mic ${
      on
        ? 'border-b4l-blue bg-b4l-blue-light text-b4l-blue-dark'
        : 'border-b4l-border bg-b4l-surface text-b4l-text-muted'
    }`

  return (
    <div className="grid gap-3 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4">
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filtre">
          <select
            id="echipa-departament"
            aria-label="Departament"
            value={dep}
            onChange={(e) => setDep(e.target.value)}
            className="rounded-md border border-b4l-border bg-b4l-surface px-2 py-1 text-mic text-b4l-text"
          >
            <option value="toate">Toate departamentele</option>
            {DEPARTAMENTE.map((d) => (
              <option key={d.slug} value={d.slug}>
                {numeDepartament(d.slug)}
              </option>
            ))}
          </select>
          <button
            type="button"
            aria-pressed={doarAlocati}
            onClick={() => setDoarAlocati(!doarAlocati)}
            className={filtru(doarAlocati)}
          >
            Doar cei alocați în perioadă
          </button>
          <button
            type="button"
            aria-pressed={doarConflicte}
            onClick={() => setDoarConflicte(!doarConflicte)}
            className={filtru(doarConflicte)}
          >
            ⚠ Doar conflicte{totalConflicte > 0 ? ` · ${totalConflicte}` : ''}
          </button>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => muta(-1)}
            aria-label="O săptămână înapoi"
            className="rounded-md p-1.5 text-b4l-text-muted hover:bg-b4l-bg"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={() => setInceput(luni(azi))}
            className="rounded-md px-2 py-1 text-mic text-b4l-text-muted hover:bg-b4l-bg"
          >
            Azi
          </button>
          <span className="px-1 text-mic tabular-nums text-b4l-text-muted">
            {ziScurt(zile[0]!)} – {ziScurt(zile[ZILE - 1]!)}
          </span>
          <button
            type="button"
            onClick={() => muta(1)}
            aria-label="O săptămână înainte"
            className="rounded-md p-1.5 text-b4l-text-muted hover:bg-b4l-bg"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {q.isLoading ? (
        <p className="px-4 text-mic text-b4l-text-muted">Se încarcă…</p>
      ) : q.isError ? (
        <p className="px-4 text-mic text-b4l-red-dark">
          Calendarul echipei n-a putut fi încărcat.{' '}
          <button type="button" onClick={() => q.refetch()} className="underline">
            Reîncearcă
          </button>
        </p>
      ) : oameni.length === 0 ? (
        <p className="px-4 py-6 text-center text-mic text-b4l-text-muted">
          {doarConflicte
            ? 'Niciun conflict în perioada asta.'
            : doarAlocati
              ? 'Nimeni nu e alocat la vreo activitate în perioada asta.'
              : 'Niciun coleg pentru filtrul ales.'}
        </p>
      ) : (
        <>
          {/* Ecran lat: grila. */}
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full min-w-[900px] border-collapse text-[11.5px]">
              <thead>
                <tr className="bg-b4l-bg-soft text-b4l-text-muted">
                  <th className="sticky left-0 z-[1] w-44 bg-b4l-bg-soft px-3 py-1.5 text-left font-semibold">
                    Coleg
                  </th>
                  {zile.map((z) => (
                    <th
                      key={z}
                      className={`border-l border-b4l-border px-1 py-1.5 text-center font-semibold ${
                        z === aziIso ? 'text-b4l-blue-dark' : weekend(z) ? 'text-b4l-text-dim' : ''
                      }`}
                    >
                      {ziScurt(z)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {oameni.map(({ o, m }) => (
                  <tr key={o.userId} className="border-t border-b4l-border">
                    <th
                      scope="row"
                      className="sticky left-0 z-[1] bg-b4l-surface px-3 py-1 text-left font-medium text-b4l-text"
                    >
                      <span className="block truncate">{o.nume}</span>
                      <span className="block truncate text-[10.5px] font-normal text-b4l-text-dim">
                        {o.functie ?? numeDepartament(departamentulRolului(o.rolUser) ?? 'Admin')}
                      </span>
                    </th>
                    {zile.map((z) => (
                      <Celula
                        key={z}
                        alocari={m.get(z) ?? []}
                        absent={o.absente.includes(z)}
                        absenta={absenta}
                        weekend={weekend(z)}
                        nume={o.nume}
                        zi={z}
                      />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Telefon: lista pe zile, doar zilele cu ceva. */}
          <div className="grid gap-3 px-4 sm:hidden">
            {!zile.some((z) => oameni.some(({ o, m }) => m.has(z) || o.absente.includes(z))) && (
              <p className="py-4 text-center text-mic text-b4l-text-muted">
                Nimeni nu e alocat sau absent în aceste două săptămâni.
              </p>
            )}
            {zile
              .filter((z) => oameni.some(({ o, m }) => m.has(z) || o.absente.includes(z)))
              .map((z) => (
                <div key={z} className="grid gap-1.5">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-b4l-text-dim">
                    {ziScurt(z)}
                    {z === aziIso && ' · azi'}
                  </span>
                  {oameni
                    .filter(({ o, m }) => m.has(z) || o.absente.includes(z))
                    .map(({ o, m }) => {
                      const l = m.get(z) ?? []
                      const absent = o.absente.includes(z)
                      return (
                        <div
                          key={o.userId}
                          className="flex items-center justify-between gap-2 rounded-md bg-b4l-bg-soft px-2.5 py-1.5 text-mic"
                        >
                          <span className="min-w-0 truncate text-b4l-text">
                            {o.nume}
                            {l.length === 1 && (
                              <span className="text-b4l-text-muted"> · {l[0]!.titlu}</span>
                            )}
                          </span>
                          {l.length > 1 ? (
                            <span className={`rounded-full px-2 text-[11px] ${TON.rosu.pastila}`}>
                              ⚠ {l.length} locuri
                            </span>
                          ) : absent ? (
                            <span className={`rounded-full px-2 text-[11px] ${TON.galben.pastila}`}>
                              ⛱ {absenta.toLowerCase()}
                            </span>
                          ) : (
                            <span className="text-[11px] text-b4l-text-dim">{l[0]?.rol ?? ''}</span>
                          )}
                        </div>
                      )
                    })}
                </div>
              ))}
          </div>
        </>
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1.5 px-4 text-[11.5px] text-b4l-text-muted">
        {tipuri.map((t) => (
          <span key={t} className="inline-flex items-center gap-1.5">
            <i className={`inline-block h-3 w-3 rounded-sm ${CULOARE_TIP[t]}`} />
            {ETICHETE_TIP[t]}
          </span>
        ))}
        {q.data?.cuAbsente && (
          <span className="inline-flex items-center gap-1.5">
            <i className={`inline-block h-3 w-3 rounded-sm ${TON.galben.pastila}`} />⛱ {absenta}
          </span>
        )}
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-3 w-3 rounded-sm border border-dashed border-b4l-text-dim" />
          Activitate încă propunere
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-3 w-3 rounded-sm outline outline-2 -outline-offset-2 outline-b4l-amber" />
          Alocat într-o zi în care lipsește
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-3 w-3 rounded-sm outline outline-2 -outline-offset-2 outline-b4l-red" />
          ⚠ Două locuri în aceeași zi
        </span>
      </div>
    </div>
  )
}

function Celula({
  alocari,
  absent,
  absenta,
  weekend,
  nume,
  zi,
}: {
  alocari: Alocare[]
  absent: boolean
  absenta: string
  weekend: boolean
  nume: string
  zi: string
}) {
  const baza = `border-l border-b4l-border p-0.5 align-middle ${weekend ? 'bg-b4l-bg-soft' : ''}`
  if (alocari.length === 0)
    return (
      <td className={baza}>
        {absent && (
          <span
            title={`${nume}: ${absenta.toLowerCase()}, ${ziScurt(zi)}`}
            className={`block truncate rounded px-1 py-1 text-[10.5px] font-semibold ${TON.galben.pastila} bg-[repeating-linear-gradient(135deg,transparent_0_5px,rgb(var(--b4l-surface)/0.6)_5px_10px)]`}
          >
            ⛱
          </span>
        )}
      </td>
    )
  const a = alocari[0]!
  const dublu = alocari.length > 1
  const peste = absent // alocat intr-o zi in care lipseste
  const titlu = `${nume}, ${ziScurt(zi)}: ${alocari.map((x) => x.titlu).join(' și ')}${
    peste ? ` (${absenta.toLowerCase()})` : ''
  }`
  return (
    <td className={baza}>
      <Link
        to={`/marketing/evenimente/${a.campanieId}`}
        title={titlu}
        aria-label={titlu}
        className={`block truncate rounded px-1 py-1 text-[10.5px] font-semibold ${
          a.provizorie
            ? 'border border-dashed border-current bg-transparent text-b4l-text-muted'
            : CULOARE_TIP[a.tip]
        } ${
          // Rosu doar pentru doua locuri; alocarea intr-o zi de absenta are conturul absentei.
          dublu
            ? 'outline outline-2 -outline-offset-2 outline-b4l-red'
            : peste
              ? 'outline outline-2 -outline-offset-2 outline-b4l-amber'
              : ''
        }`}
      >
        {dublu ? `⚠ ${alocari.length} locuri` : peste ? `⛱ ${a.titlu}` : a.titlu}
      </Link>
    </td>
  )
}
